import { X, Sparkles, Wand2, FilePlus } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { Overline } from "@/components/ui-kit";

/**
 * Method picker — the first step when creating a new project.
 * Shows three options: Interview / Creative / Manual.
 */
export default function CreateProjectFlow({ onClose, onChoose }) {
  const { t } = useLang();

  const options = [
    {
      key: "interview",
      icon: Sparkles,
      title: t("flow.interview.title"),
      desc: t("flow.interview.desc"),
      cta: t("flow.interview.cta"),
      accent: "bg-[#F5EFE6] border-[#B89154]/40 text-[#B89154]",
      featured: true,
    },
    {
      key: "creative",
      icon: Wand2,
      title: t("flow.creative.title"),
      desc: t("flow.creative.desc"),
      cta: t("flow.creative.cta"),
      accent: "bg-[#2563EB]/5 border-[#2563EB]/30 text-[#2563EB]",
    },
    {
      key: "manual",
      icon: FilePlus,
      title: t("flow.manual.title"),
      desc: t("flow.manual.desc"),
      cta: t("flow.manual.cta"),
      accent: "bg-white border-[#E8E6E1] text-[#5C5B5A]",
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-[#1A1A1A]/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="create-flow-overlay"
    >
      <div
        className="bg-[#F9F8F6] w-full max-w-4xl rounded-sm shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-8 py-5 border-b border-[#E8E6E1] flex items-center justify-between">
          <Overline>{t("dashboard.new_project")}</Overline>
          <button
            onClick={onClose}
            className="text-[#8A8886] hover:text-[#1A1A1A] transition-colors"
            data-testid="create-flow-close-btn"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-8 py-10">
          <div className="text-center mb-10">
            <h2
              className="text-4xl tracking-tight text-[#1A1A1A]"
              style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
            >
              {t("flow.title")}
            </h2>
            <p className="mt-3 text-sm text-[#5C5B5A] max-w-2xl mx-auto">{t("flow.subtitle")}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {options.map((o) => {
              const Icon = o.icon;
              return (
                <button
                  key={o.key}
                  onClick={() => onChoose(o.key)}
                  className={`group text-left border-2 rounded-sm p-6 transition-all hover:-translate-y-0.5 hover:shadow-md flex flex-col gap-4 min-h-[260px] ${
                    o.featured
                      ? "border-[#B89154] bg-[#F5EFE6]/60 hover:bg-[#F5EFE6]"
                      : "border-[#E8E6E1] bg-white hover:border-[#2563EB]"
                  }`}
                  data-testid={`flow-choose-${o.key}`}
                >
                  <div className={`inline-flex h-10 w-10 items-center justify-center rounded-sm border ${o.accent}`}>
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <h3
                      className="text-xl text-[#1A1A1A] leading-tight"
                      style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
                    >
                      {o.title}
                    </h3>
                    <p className="mt-2 text-sm text-[#5C5B5A] leading-relaxed">{o.desc}</p>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-[#E8E6E1]">
                    <span className={`text-xs tracking-[0.2em] uppercase ${o.featured ? "text-[#B89154]" : "text-[#2563EB]"}`}>
                      {o.cta}
                    </span>
                    <span className={`text-lg ${o.featured ? "text-[#B89154]" : "text-[#2563EB]"}`}>→</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
